import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { catchError, forkJoin, map, Observable, of, shareReplay } from "rxjs";
import { environment } from "../../environments/environment";
import { Marketplace } from "../models/marketplace.model";
import { MarketplacePriority } from "../models/marketplace-priority.model";
import { MarketplaceDeliveryStats } from "../models/marketplace-delivery-stats.model";
import { MarketplaceDeliveryError } from "../models/marketplace-delivery-error.model";
import { MarketplaceChangelog } from "../models/marketplace-changelog.model";
import { Page } from "../models/page.model";

/**
 * PURPOSE: responsible for making all marketplace related HTTP API calls
 */
@Injectable({
  providedIn: "root"
})
export class MarketplaceService {
  constructor(private http: HttpClient) {}

  /**
   * Retrieves a List of Marketplaces
   */
  public getMarketplaces(): Observable<Marketplace[]> {
    return this.http
      .get<
        Marketplace[]
      >(`${environment.fulcrumUrl}/api/v3/marketplaces`, this.buildBasicHttpOptions())
      .pipe(
        catchError((error: HttpErrorResponse): Observable<Marketplace[]> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of(new Array<Marketplace>());
          }
          console.log(error);
          throw error;
        })
      );
  }

  /**
   * Retrieves a specific marketplace
   * @param marketplaceId the ID of the marketplace to retrieve
   */
  public getMarketplace(marketplaceId: number): Observable<Marketplace> {
    return this.http
      .get<Marketplace>(
        `${environment.fulcrumUrl}/api/v3/marketplaces/${marketplaceId}`,
        this.buildBasicHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<Marketplace> => {
          console.log(error);
          throw error;
        })
      );
  }

  /**
   * Creates a new marketplace
   * @param marketplace the object containing the marketplace updates
   */
  public createMarketplace(marketplace: Marketplace): Observable<Marketplace> {
    return this.http
      .post<Marketplace>(
        `${environment.fulcrumUrl}/api/v3/marketplaces`,
        marketplace,
        this.buildBasicHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<Marketplace> => {
          console.log(error);
          throw error;
        })
      );
  }

  /**
   * Updates as specific marketplace
   * @param marketplace the object containing the marketplace updates
   */
  public updateMarketplace(marketplace: Marketplace): Observable<null> {
    return this.http
      .post<null>(
        `${environment.fulcrumUrl}/api/v3/marketplaces/${marketplace.id}`,
        marketplace,
        this.buildBasicHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<null> => {
          console.log(error);
          throw error;
        })
      );
  }

  /**
   * Updates multiple marketplaces in parallel for optimal performance
   * @param marketplaces array of marketplace objects to update
   * @returns Observable that completes when all updates are finished
   */
  public updateMarketplaces(marketplaces: Marketplace[]): Observable<null> {
    if (!marketplaces || marketplaces.length === 0) {
      return of(null);
    }

    // Create an array of update observables for parallel execution
    const updateObservables: Observable<null>[] = marketplaces.map(
      (marketplace: Marketplace): Observable<null> => this.updateMarketplace(marketplace)
    );

    // Execute all requests in parallel and wait for all to complete
    return forkJoin(updateObservables).pipe(
      map((): null => null),
      catchError((error: HttpErrorResponse): Observable<null> => {
        console.log("Error updating marketplaces:", error);
        throw error;
      })
    );
  }

  /**
   * Updates priorities for all marketplaces
   * @param marketplacePriorities array of marketplace IDs and their new priorities
   */
  public updateMarketplacePriorities(
    marketplacePriorities: MarketplacePriority[]
  ): Observable<null> {
    return this.http
      .post<null>(
        `${environment.fulcrumUrl}/api/v3/marketplaces/priorities`,
        marketplacePriorities,
        this.buildBasicHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<null> => {
          console.log(error);
          throw error;
        })
      );
  }

  /**
   * Retrieves a list of the delivery statistics for all marketplaces
   */
  public getDeliveryStatsForMarketplaces(): Observable<MarketplaceDeliveryStats[]> {
    return this.http
      .get<
        MarketplaceDeliveryStats[]
      >(`${environment.fulcrumUrl}/api/v3/marketplaces/delivery-stats`, this.buildBasicHttpOptions())
      .pipe(
        catchError((error: HttpErrorResponse): Observable<MarketplaceDeliveryStats[]> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of(new Array<MarketplaceDeliveryStats>());
          }
          console.log(error);
          throw error;
        }),
        // Use shareReplay to ensure multiple subscribers get the same data without triggering multiple HTTP requests
        shareReplay(1)
      );
  }

  /**
   * Retrieves a page of the delivery errors for all marketplaces
   * @param page the page number (0-based)
   * @param size the page size (number of records per page)
   * @param onlyDeliveryErrors when true fetches only DELIVERY_ERROR records; when false fetches both DELIVERY_ERROR and REJECTED_BY_PARTNER
   */
  public getDeliveryErrorsForMarketplaces(
    page: number = 0,
    size: number = 10,
    onlyDeliveryErrors: boolean = false
  ): Observable<Page<MarketplaceDeliveryError>> {
    const params: HttpParams = new HttpParams()
      .set("page", page.toString())
      .set("size", size.toString())
      .set("onlyDeliveryErrors", onlyDeliveryErrors.toString());

    const options = {
      ...this.buildBasicHttpOptions(),
      params: params
    };

    return this.http
      .get<
        Page<MarketplaceDeliveryError>
      >(`${environment.fulcrumUrl}/api/v3/marketplaces/delivery-errors`, options)
      .pipe(
        catchError((error: HttpErrorResponse): Observable<Page<MarketplaceDeliveryError>> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of();
          }
          console.log(error);
          throw error;
        })
      );
  }

  public getMarketplaceChangelog(
    marketplaceId: number,
    page: number = 0,
    size: number = 20
  ): Observable<Page<MarketplaceChangelog>> {
    const params: HttpParams = new HttpParams()
      .set("page", page.toString())
      .set("size", size.toString());

    const options = { ...this.buildBasicHttpOptions(), params };

    return this.http
      .get<Page<MarketplaceChangelog>>(
        `${environment.fulcrumUrl}/api/v3/marketplaces/${marketplaceId}/changelog`,
        options
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<Page<MarketplaceChangelog>> => {
          console.log(error);
          throw error;
        })
      );
  }

  private buildBasicHttpOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization:
          "Basic " + btoa(`${environment.claimItApiUsername}:${environment.claimItApiPassword}`)
      }),
      params: {}
    };
  }
}
